/* eslint-disable no-console -- This is a debug/test page for local development */
import { useState, useCallback } from 'react';

import Box from '@zenbusiness/zui/Box';
import Text from '@zenbusiness/zui/Text';

import { VELO_QA_CONFIGS } from '#client/components/utils/constants';
import VeloQA from '#client/components/VeloQA';

/**
 * VeloCardTest Page
 *
 * Test page for locally developing and testing the VeloQA component.
 * Displays all available VeloQA configurations for visual testing.
 *
 * Access via: /guide/velo-card-test
 * @returns {import('react').ReactElement} The VeloCardTest component
 */
export default function VeloCardTest() {
  const [threadInfo, setThreadInfo] = useState({});

  const handleThreadCreated = useCallback(
    (configKey) => (threadUuid) => {
      console.log(`[VeloCardTest] Thread created for ${configKey}:`, threadUuid);
      setThreadInfo((prev) => ({
        ...prev,
        [configKey]: { threadUuid, createdAt: new Date().toISOString() }
      }));
    },
    []
  );

  const handleResponseReceived = useCallback(
    (configKey) => (instructions, threadUuid) => {
      console.log(`[VeloCardTest] Response received for ${configKey}:`, {
        instructions,
        threadUuid
      });
      setThreadInfo((prev) => ({
        ...prev,
        [configKey]: {
          ...prev[configKey],
          lastResponse: new Date().toISOString(),
          instructionCount: instructions.length
        }
      }));
    },
    []
  );

  return (
    <Box
      sx={{
        padding: 4,
        backgroundColor: (theme) => theme.palette.background.default,
        minHeight: '100vh',
        maxWidth: '90rem',
        marginInline: 'auto'
      }}
    >
      {/* Header */}
      <Box sx={{ marginBottom: 4 }}>
        <Text size="xl" weight="bold">
          VeloCard Component Test Page
        </Text>
        <Text color="secondary" mt={1} size="sm">
          Testing all VeloQA configurations in local development
        </Text>
      </Box>

      {/* Thread Info Debug Panel */}
      {Object.keys(threadInfo).length > 0 && (
        <Box
          sx={{
            marginBottom: 4,
            padding: 2,
            backgroundColor: (theme) => theme.palette.background.paper,
            borderRadius: '8px',
            border: (theme) => `1px solid ${theme.palette.divider}`
          }}
        >
          <Text size="sm" weight="bold">
            Thread Debug Info:
          </Text>
          <pre
            style={{
              fontSize: '12px',
              overflow: 'auto',
              marginTop: '8px',
              maxHeight: '200px'
            }}
          >
            {JSON.stringify(threadInfo, null, 2)}
          </pre>
        </Box>
      )}

      {/* VeloCard Examples */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(600px, 1fr))',
          gap: 4
        }}
      >
        {Object.keys(VELO_QA_CONFIGS).map((configKey) => (
          <Box key={configKey}>
            <Text mb={2} size="md" weight="bold">
              Config: {configKey}
            </Text>
            <VeloQA
              configKey={configKey}
              onResponseReceived={handleResponseReceived(configKey)}
              onThreadCreated={handleThreadCreated(configKey)}
            />
          </Box>
        ))}
      </Box>

      {/* Custom Props Example */}
      <Box sx={{ marginTop: 4 }}>
        <Text mb={2} size="md" weight="bold">
          Custom Props Example
        </Text>
        <VeloQA
          configKey="general-help"
          ctaText="Continue in Velo Chat"
          subtitle="This is a custom subtitle to test prop overrides."
          title="Custom Title Test"
        />
      </Box>

      {/* Disabled Example */}
      <Box sx={{ marginTop: 4 }}>
        <Text mb={2} size="md" weight="bold">
          Disabled State Example
        </Text>
        <VeloQA configKey="business-licenses" disabled />
      </Box>

      {/* Without CTA Example */}
      <Box sx={{ marginTop: 4 }}>
        <Text mb={2} size="md" weight="bold">
          Without CTA Example
        </Text>
        <VeloQA configKey="formation-help" showCTA={false} />
      </Box>
    </Box>
  );
}
